import { memo, type ReactNode } from 'react';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import {
  WORKSPACE_AGENT_MENTION_ROW_KEYBOARD_RING_STYLE,
  WORKSPACE_AGENT_MENTION_ROW_UNRINGED_STYLE,
  workspaceAgentMentionRow,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMentionRowChrome';
import { WORKSPACE_AGENT_PANEL_MENTION_ROW_THUMBNAIL } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import { useWorkspaceAgentMentionRowInteraction } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionRowInteraction';

export type WorkspaceAgentMentionImageRowProps = {
  /** Drawn in the media block when there is no still; each row kind falls back to its own. */
  glyph: ReactNode;
  /** DOM id so the composer can point `aria-activedescendant` at the highlighted row. */
  id: string;
  index: number;
  /** This row holds the highlight *and* the keyboard is what last moved it, so it takes the focus ring. */
  isKeyboardHighlighted: boolean;
  isSelected: boolean;
  itemId: string;
  onHighlight: (index: number) => void;
  onPreviewAnchorElementChange?: (element: HTMLElement | null) => void;
  /** By id, not index: a same-query refetch between paint and click can move this item. */
  onSelect: (itemId: string) => void;
  thumbnailUrl: string | null;
  title: string;
};

/**
 * The chrome every flat picker row shares: a still on a gray ground square to the row's left edge,
 * then a truncated title. Which glyph stands in for a missing still, and which object the row names,
 * belong to the row that wraps this — the board tree draws its own, because it nests.
 */
export const WorkspaceAgentMentionImageRow = memo(
  ({
    glyph,
    id,
    index,
    isKeyboardHighlighted,
    isSelected,
    itemId,
    onHighlight,
    onPreviewAnchorElementChange,
    onSelect,
    thumbnailUrl,
    title,
  }: WorkspaceAgentMentionImageRowProps) => {
    const { handleClick, handleMouseDown, handleMouseEnter, rowRef } =
      useWorkspaceAgentMentionRowInteraction<HTMLButtonElement>({
        index,
        isSelected,
        itemId,
        onHighlight,
        onPreviewAnchorElementChange,
        onSelect,
      });

    return (
      <button
        aria-selected={isSelected}
        className={workspaceAgentMentionRow({ isSelected })}
        data-mention-item-id={itemId}
        data-mention-keyboard-highlight={String(isKeyboardHighlighted)}
        data-testid="WORKSPACE_AGENT_MENTION_IMAGE_ROW"
        id={id}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
        ref={rowRef}
        role="option"
        style={
          isKeyboardHighlighted
            ? WORKSPACE_AGENT_MENTION_ROW_KEYBOARD_RING_STYLE
            : WORKSPACE_AGENT_MENTION_ROW_UNRINGED_STYLE
        }
        tabIndex={-1}
        type="button"
      >
        {/*
         * Gray ground with Strato thumbnail corners (`rounded-4`, same as prototype `rounded-sm`). The row
         * carries no per-item transparency or fit information, so there is nothing to gate a checkerboard on.
         */}
        <span
          className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-4 ads:bg-subtle bg-gray-3 ads:text-icon-default text-gray-10"
          data-testid="WORKSPACE_AGENT_MENTION_IMAGE_ROW_MEDIA"
        >
          {thumbnailUrl ? (
            <WorkspaceAgentPanelImage
              {...WORKSPACE_AGENT_PANEL_MENTION_ROW_THUMBNAIL}
              alt=""
              className="relative size-full rounded-4 object-cover"
              data-testid="WORKSPACE_AGENT_MENTION_IMAGE_ROW_THUMBNAIL"
              src={thumbnailUrl}
            />
          ) : (
            glyph
          )}
        </span>
        <span
          className="min-w-0 flex-1 truncate leading-5"
          data-testid="WORKSPACE_AGENT_MENTION_IMAGE_ROW_TITLE"
          /**
           * A native title, not a `Tooltip`: tooltip content portals outside this popover, so a press
           * on it reads as an outside press and would dismiss the picker without inserting anything.
           * On the label span rather than the `option` itself, as the board row does — a `title` on
           * the row is announced as its description on top of the name it already has.
           */
          title={title}
        >
          {title}
        </span>
      </button>
    );
  },
);

WorkspaceAgentMentionImageRow.displayName = 'WorkspaceAgentMentionImageRow';
